import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {FieldConfig} from '../field.interface';
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {LiveAnnouncer} from "@angular/cdk/a11y";
import {MatChipEditedEvent, MatChipInputEvent} from "@angular/material/chips";
import {Subscription} from "rxjs";
import {first} from "rxjs/operators";

@Component({
  selector: 'app-input-chip',
  template: `
    <mat-form-field [formGroup]="group"
                    appearance="outline"
                    class="demo-full-width">
      <mat-label> {{ field.label }}</mat-label>
      <mat-chip-grid #chipGrid>
        <mat-chip-row (edited)="edit(chip, $event)"
                      (removed)="remove(chip)"
                      *ngFor="let chip of chips"
                      [aria-description]="'press enter to edit ' + chip.name"
                      [editable]="true">
          {{ chip.name }}
          <button [attr.aria-label]="'remove ' + chip.name" matChipRemove>
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip-row>
        <input
          #chipInput
          (matChipInputTokenEnd)="add($event)"
          [formControlName]="field.key"
          [matChipInputAddOnBlur]="addOnBlur"
          [matChipInputFor]="chipGrid"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [placeholder]="'Enter New '+ field.label"
          [type]="field.inputType"
          matInput
        />
        <ng-container *ngFor="let validation of field.validations" ngProjectAs="mat-error">
          <mat-error *ngIf="group.get(field.key).hasError(validation.name)">
            {{ validation.message }}
          </mat-error>
        </ng-container>
      </mat-chip-grid>
      <mat-hint align="start"><em>{{ field.hint }}</em></mat-hint>

    </mat-form-field>
  `,
  styles: [],
})
export class InputChipComponent implements OnInit, OnDestroy, AfterViewInit {
  field: FieldConfig;
  group: FormGroup;
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  chips: Chip[] = [];
  announcer = inject(LiveAnnouncer);
  sub = new Subscription();
  @ViewChild('chipInput') chipInput: any;

  constructor() {
  }

  ngOnInit() {
    const valueM: any = JSON.parse(this.group.controls[this.field.key].value);
    if (Array.isArray(valueM)) {
      this.chips = valueM;
      this.group.controls[this.field.key].setValue(valueM);
    }
  }

  ngAfterViewInit() {
    this.sub.add(
      this.group.controls[this.field.key].valueChanges.pipe(
        first()  // Use the last operator to get the last emitted value
      ).subscribe((formValue) => {
        if (Array.isArray(formValue)) {
          (formValue || []).forEach(x => {
            this.pusher(x);
          });
        }
        setTimeout(() => {
          this.clearChipInput();
        });
      })
    );
  }

  pusher(value) {
    const availableIndex = this.chips.findIndex(m => m?.name === value);
    if (availableIndex === -1 && typeof value != 'object') this.chips.push({name: value});
  }


  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();// Add our chip
    if (value) this.chips.push({name: value});
    this.setFormValue();
    event.chipInput!.clear();
  }

  remove(chip: Chip): void {
    const index = this.chips.findIndex(x => x?.name === chip?.name);
    if (index >= 0) {
      this.chips.splice(index, 1);
      this.announcer.announce(`Removed ${chip}`);
    }
  }

  edit(chip: Chip, event: MatChipEditedEvent) {
    const value = event.value.trim();
    // Remove chip if it no longer has a name
    if (!value) {
      this.remove(chip);
      return;
    }
    // Edit existing chip
    const index = this.chips.findIndex(x => x?.name === chip?.name);
    if (index != -1) this.chips[index].name = value;
  }

  setFormValue() {
    this.chips = this.chips.filter(y => y?.name);
    this.group.controls[this.field.key].setValue(this.chips);
  }

  clearChipInput(): void {
    if (this.chipInput) {
      this.chipInput.nativeElement.value = '';
    }
  }
}

export interface Chip {
  name: string;

  [kwy: string]: any;
}
